import { Component, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { AbstractControl, FormArrayName, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { CustomValidatorMessage, LgdFormControlFactory, LgdPatternValidator, LgdTextComponent } from "@tylertech/lgd-reactive-forms";
import { Address } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

/**
 * Defines a Validator and matching message requiring the value of the FormControl to match the pattern like "(999) 999-9999x9999999".
 * TODO - This might not be a SYSTEM-wide standard. It was implemented for the UB apps.
 */
export class PostalCodeValidator extends LgdPatternValidator
{
    constructor()
    {
        //99999-?9?9?9?9?
        super(/^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] *\d[A-Z]\d)$/);
    }

    /** @override */
    getErrorMessage(label: string): ReadonlyArray<CustomValidatorMessage>
    {
        return [{
            errorKey: "pattern",
            message: `${label} is invalid.`,
        },];
    }
}

@Component({
    selector: "address",
    templateUrl: "./address.tmpl.html",
    imports: [
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        LgdTextComponent,
        ReactiveFormsModule
    ]
})
export class AddressComponent
    implements OnInit, OnDestroy
{
    private readonly formArrayNameDirective = inject(FormArrayName, { optional: true });

    @Input() readonly model: Address;

    private readonly destructor$ = new Subject<void>();
    readonly form_address: FormGroup;

    constructor()
    {
        this.form_address = new FormGroup({
            attention: LgdFormControlFactory.text(undefined, false, {}),
            line1: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 50,
                }),
            line2: LgdFormControlFactory.text(
                undefined,
                false,
                {
                    maxLength: 50,
                }),
            city: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 50,
                }),
            state: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 2,
                }),
            postalCode: LgdFormControlFactory.text(
                undefined,
                true,
                {},
                [
                    new PostalCodeValidator()
                ]),
        });
    }

    get Attention(): string { return this.model.Attention; }
    set Attention(value: string) { this.model.Attention = value; }

    get Line1(): string { return this.model.Line1; }
    set Line1(value: string) { this.model.Line1 = value; }

    get Line2(): string { return this.model.Line2; }
    set Line2(value: string) { this.model.Line2 = value; }

    get City(): string { return this.model.City; }
    set City(value: string) { this.model.City = value; }

    get State(): string { return this.model.State; }
    set State(value: string) { this.model.State = value; }

    get PostalCode(): string { return this.model.Zip; }
    set PostalCode(value: string) { this.model.Zip = value; }

    private get attentionControl(): AbstractControl<unknown> { return this.form_address.get("attention"); }
    private get line1Control(): AbstractControl<unknown> { return this.form_address.get("line1"); }
    private get line2Control(): AbstractControl<unknown> { return this.form_address.get("line2"); }
    private get cityControl(): AbstractControl<unknown> { return this.form_address.get("city"); }
    private get stateControl(): AbstractControl<unknown> { return this.form_address.get("state"); }
    private get postalCodeControl(): AbstractControl<unknown> { return this.form_address.get("postalCode"); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        if (this.formArrayNameDirective?.control)
        {
            this.formArrayNameDirective.control.push(this.form_address);
        }

        this.attentionControl.setValue(this.Attention);
        subscribeToValueChanges(this.attentionControl, (newValue: string) =>
        {
            this.Attention = newValue;
        });

        this.line1Control.setValue(this.Line1);
        subscribeToValueChanges(this.line1Control, (newValue: string) =>
        {
            this.Line1 = newValue;
        });

        this.line2Control.setValue(this.Line2);
        subscribeToValueChanges(this.line2Control, (newValue: string) =>
        {
            this.Line2 = newValue;
        });

        this.cityControl.setValue(this.City);
        subscribeToValueChanges(this.cityControl, (newValue: string) =>
        {
            this.City = newValue;
        });

        this.stateControl.setValue(this.State);
        subscribeToValueChanges(this.stateControl, (newValue: string) =>
        {
            this.State = newValue;
        });

        this.postalCodeControl.setValue(this.PostalCode);
        subscribeToValueChanges(this.postalCodeControl, (newValue: string) =>
        {
            this.PostalCode = newValue;
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }
}
